import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getGlossaryForSet,
  getLessonGlossary,
  getLessonStandards,
  getStandardSets,
  getStandardsForSet,
  putLessonGlossary,
  putLessonStandards,
  type GlossaryTermRecord,
  type LessonGlossaryLink,
  type LessonStandardLink,
  type StandardRecord,
  type StandardSetSummary,
} from "../../auth/api-client";
import { useAuth } from "../../auth/AuthContext";

export interface LessonStandardsSelection {
  loading: boolean;
  error: string | null;
  standardSets: StandardSetSummary[];
  selectedSetId: string;
  selectSet: (id: string) => void;
  standardsInSet: StandardRecord[];
  glossaryInSet: GlossaryTermRecord[];
  selectedStandardIds: Set<string>;
  selectedGlossaryIds: Set<string>;
  toggleStandard: (id: string) => void;
  toggleGlossary: (id: string) => void;
  /** Standards/glossary selected in the active set, with full detail (for AI prompts). */
  selectedStandards: StandardRecord[];
  selectedGlossary: GlossaryTermRecord[];
  /** All standards this lesson is tagged with (across sets). */
  linkedStandards: LessonStandardLink[];
  /** All glossary terms this lesson is tagged with (across sets). */
  linkedGlossary: LessonGlossaryLink[];
  hasSelection: boolean;
  /** Persist current selection to a saved lesson. */
  persist: (lessonId: string) => Promise<void>;
  /** Pre-select by standard code / glossary term (used by AI retroactive tagging). */
  applyAiAlignment: (standardCodes: string[], glossaryTerms: string[]) => void;
}

export function useLessonStandardsSelection(
  lessonId: string | null,
  initial?: { standardSetId?: string; standardId?: string }
): LessonStandardsSelection {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();

  const [standardSets, setStandardSets] = useState<StandardSetSummary[]>([]);
  const [selectedSetId, setSelectedSetId] = useState("");
  const [standardsInSet, setStandardsInSet] = useState<StandardRecord[]>([]);
  const [glossaryInSet, setGlossaryInSet] = useState<GlossaryTermRecord[]>([]);
  const [selectedStandardIds, setSelectedStandardIds] = useState<Set<string>>(new Set());
  const [selectedGlossaryIds, setSelectedGlossaryIds] = useState<Set<string>>(new Set());
  const [linkedStandards, setLinkedStandards] = useState<LessonStandardLink[]>([]);
  const [linkedGlossary, setLinkedGlossary] = useState<LessonGlossaryLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [linksLoaded, setLinksLoaded] = useState(() => !lessonId);
  const seededRef = useRef(false);

  // Load published sets + any existing lesson links once.
  useEffect(() => {
    let cancelled = false;
    if (!bearer) return;
    (async () => {
      setLoading(true);
      setError(null);
      if (lessonId) setLinksLoaded(false);
      try {
        const { standardSets: sets } = await getStandardSets(bearer);
        if (cancelled) return;
        setStandardSets(sets);

        let initialSetId = sets[0]?.id ?? "";
        if (lessonId && !seededRef.current) {
          const [{ standards }, { glossary }] = await Promise.all([
            getLessonStandards(bearer, lessonId),
            getLessonGlossary(bearer, lessonId),
          ]);
          if (cancelled) return;
          if (standards.length > 0) initialSetId = standards[0].standardSetId;
          else if (glossary.length > 0) initialSetId = glossary[0].standardSetId;
          setSelectedStandardIds(new Set(standards.map((s) => s.id)));
          setSelectedGlossaryIds(new Set(glossary.map((g) => g.id)));
          setLinkedStandards(standards);
          setLinkedGlossary(glossary);
          seededRef.current = true;
        } else if (!lessonId && !seededRef.current && initial?.standardSetId) {
          if (sets.some((s) => s.id === initial.standardSetId)) initialSetId = initial.standardSetId;
          if (initial.standardId) setSelectedStandardIds(new Set([initial.standardId]));
          seededRef.current = true;
        }
        setSelectedSetId(initialSetId);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load standards");
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLinksLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer, lessonId, initial?.standardSetId, initial?.standardId]);

  // Load standards/glossary for the active set.
  useEffect(() => {
    let cancelled = false;
    if (!bearer || !selectedSetId) {
      setStandardsInSet([]);
      setGlossaryInSet([]);
      return;
    }
    (async () => {
      try {
        const [{ standards }, { glossary }] = await Promise.all([
          getStandardsForSet(bearer, selectedSetId),
          getGlossaryForSet(bearer, selectedSetId),
        ]);
        if (cancelled) return;
        setStandardsInSet(standards);
        setGlossaryInSet(glossary);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load set");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer, selectedSetId]);

  const toggleStandard = useCallback(
    (id: string) => {
      const std = standardsInSet.find((s) => s.id === id);
      const setName = standardSets.find((s) => s.id === selectedSetId)?.name ?? "";
      setSelectedStandardIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      if (!std) return;
      setLinkedStandards((prev) => {
        const exists = prev.some((s) => s.id === id);
        if (exists) return prev.filter((s) => s.id !== id);
        return [
          ...prev,
          {
            id: std.id,
            code: std.code,
            description: std.description,
            standardSetId: selectedSetId,
            standardSetName: setName,
            retired: false,
          },
        ];
      });
    },
    [selectedSetId, standardSets, standardsInSet]
  );

  const toggleGlossary = useCallback(
    (id: string) => {
      const term = glossaryInSet.find((g) => g.id === id);
      const setName = standardSets.find((s) => s.id === selectedSetId)?.name ?? "";
      setSelectedGlossaryIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      if (!term) return;
      setLinkedGlossary((prev) => {
        const exists = prev.some((g) => g.id === id);
        if (exists) return prev.filter((g) => g.id !== id);
        return [
          ...prev,
          {
            id: term.id,
            term: term.term,
            definition: term.definition,
            standardSetId: selectedSetId,
            standardSetName: setName,
            retired: false,
          },
        ];
      });
    },
    [glossaryInSet, selectedSetId, standardSets]
  );

  const applyAiAlignment = useCallback(
    (standardCodes: string[], glossaryTerms: string[]) => {
      const codeSet = new Set(standardCodes.map((c) => c.trim().toLowerCase()).filter(Boolean));
      const termSet = new Set(glossaryTerms.map((t) => t.trim().toLowerCase()).filter(Boolean));
      if (codeSet.size > 0) {
        setSelectedStandardIds((prev) => {
          const next = new Set(prev);
          for (const s of standardsInSet) if (codeSet.has(s.code.trim().toLowerCase())) next.add(s.id);
          return next;
        });
      }
      if (termSet.size > 0) {
        setSelectedGlossaryIds((prev) => {
          const next = new Set(prev);
          for (const g of glossaryInSet) if (termSet.has(g.term.trim().toLowerCase())) next.add(g.id);
          return next;
        });
      }
    },
    [standardsInSet, glossaryInSet]
  );

  const persist = useCallback(
    async (id: string) => {
      if (!bearer) return;
      if (loading || !linksLoaded) {
        throw new Error("Standards are still loading. Wait a moment and save again.");
      }
      await Promise.all([
        putLessonStandards(bearer, id, [...selectedStandardIds]),
        putLessonGlossary(bearer, id, [...selectedGlossaryIds]),
      ]);
    },
    [bearer, loading, linksLoaded, selectedStandardIds, selectedGlossaryIds]
  );

  const selectedStandards = useMemo(
    () => standardsInSet.filter((s) => selectedStandardIds.has(s.id)),
    [standardsInSet, selectedStandardIds]
  );
  const selectedGlossary = useMemo(
    () => glossaryInSet.filter((g) => selectedGlossaryIds.has(g.id)),
    [glossaryInSet, selectedGlossaryIds]
  );

  return {
    loading,
    error,
    standardSets,
    selectedSetId,
    selectSet: setSelectedSetId,
    standardsInSet,
    glossaryInSet,
    selectedStandardIds,
    selectedGlossaryIds,
    toggleStandard,
    toggleGlossary,
    selectedStandards,
    selectedGlossary,
    linkedStandards,
    linkedGlossary,
    hasSelection: selectedStandardIds.size > 0 || selectedGlossaryIds.size > 0,
    persist,
    applyAiAlignment,
  };
}
